<template>
    <div class="container my-12">
        <h1 class="mb-3 text-2xl font-medium">{{ translate('page_title') }}</h1>
        <div class="grid grid-cols-2 gap-10 mb-12 mobile:block">
            <div class="tablet:text-sm mobile:hidden">
                <p>
                    {{ translate('page_intro') }}
                </p>
            </div>

            <div v-if="accountStore.hasJoinLink && accountStore.canSeeShareLink">
                <p class="text-sm tablet:text-xs">
                    {{ translate('join_link_intro') }}
                </p>

                <div class="mt-3 flex w-full">
                    <input class="bg-white !rounded-r-none !border-r-0"
                           :value="accountStore.joinLink"
                           type="text"
                           ref="joinLink"
                           readonly v-on:click="selectJoinLink()"/>
                    <a href="" v-on:click.prevent="copyJoinLink()"
                       class="btn btn-default rounded-l-none w-40 text-center">
                        <i class="fas fa-clipboard mr-2"></i> {{ translate('copy') }}
                    </a>
                </div>
            </div>
        </div>

        <div class="my-10" v-if="accountStore.userLimitReached">
            <div
                class="w-1/2 mobile:w-full items-center rounded-xl shadow-lg p-6 border border-grey-200">
                <div>
                    <div class="text-md font-semibold mb-3">
                        {{ translate('upgrade_users_title') }}
                    </div>
                    <p v-if="accountStore.showExtendedUpsellMessage" class="text-sm mb-6">
                        {{ translate('upgrade_users_body', accountStore.userLimit) }}
                    </p>
                    <a :href="accountStore.upgradeUsersLink" class="btn btn-primary">{{ translate('upgrade_users_btn') }} <i
                        class="fas fa-angle-right ml-2"></i></a>
                </div>
            </div>
        </div>

        <div class="mb-3" v-if="accountStore.needReviewUsers.length > 0 && accountStore.isLoggedInAsAdmin">
            <needs-review-user v-for="(user, index) in accountStore.needReviewUsers"
                               :key="index"
                               :index="index"
                               :user="user">
            </needs-review-user>
        </div>

        <div class="grid grid-flow-col auto-cols-fr mb-3 gap-5 ">
            <div  class="mobile:hidden">
                <input type="text" :placeholder="translate('filter_placeholder')" v-model="filterTerm"/>
            </div>
            <div class="my-auto text-center text-sm text-grey-800">
                {{ accountStore.activeUsers.length }} / {{ accountStore.userLimit }}
                {{ translate('users').toLowerCase() }}
            </div>
            <div class="text-right" v-if="accountStore.isLoggedInAsAdmin && accountStore.canAddUsers">
                <a href="" class="btn btn-primary" v-on:click.prevent="openAddUserModal">
                    <i class="fas fa-plus"></i> &nbsp;{{ translate('gebruiker_toevoegen') }}
                </a>
            </div>
        </div>

        <table>
            <thead>
            <tr>
                <th class="w-24 mobile:hidden">{{ translate('beheerder') }}?</th>
                <th class="w-44">{{ translate('naam') }}</th>
                <th class="w-28">{{ translate('status') }}</th>
                <th class="w-72">{{ translate('email') }}</th>
                <th class="w-56 tablet:hidden">{{ translate('laatst_actief') }}</th>
                <th class="w-12 tablet:hidden" v-if="accountStore.isLoggedInAsAdmin && accountStore.canSeeMobiles"><i :title="translate('aantal_mobile')"
                                                             class="far fa-mobile-alt"></i></th>
                <th class="w-12 tablet:hidden"><i :title="translate('aantal_persoonlijke_alerts_gebruikt')" class="far fa-bell"></i>
                </th>
                <th class="mobile:hidden"></th>
                <th class="tablet:hidden"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="user in filteredUsers"
                :user="user"
                is="vue:user-row"
                :key="user.userId"></tr>
            </tbody>
        </table>
        
        <add-user-modal></add-user-modal>
        <review-user-modal v-if="accountStore.reviewedUser" :user="accountStore.reviewedUser"></review-user-modal>
        <edit-user-modal v-if="accountStore.edittedUser" :user="accountStore.edittedUser"></edit-user-modal>
    </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import Validate from '../../../directives/validate';
import NeedsReviewUser from './needs-review-user.vue';
import UserRow from './user-row.vue';
import { RadarAccessLevel, User} from './types';
import {Language} from '../../../types/dto/Language';
import {useAccountStore} from "../store";
import Toast from "../../../lib/Toast";
import _ from 'lodash';
import ReviewUserModal from "./review-user-modal.vue";
import EditUserModal from "./edit-user-modal.vue";
import AddUserModal from "./add-user-modal.vue";


export default defineComponent({
    name: 'account-users',
    directives: {
        Validate
    },
    components: {
        AddUserModal,
        EditUserModal,
        ReviewUserModal,
        UserRow,
        NeedsReviewUser,
    },
    data() {
        return {
            Language,
            RadarAccessLevel,
            filterTerm: '',
            accountStore: useAccountStore()
        };
    },
    computed: {
        filteredUsers(): User[] {
            return _.orderBy(
                _.filter(this.accountStore.users, (user) => {
                    return user.type !== "review-pending" && (user.name.contains(this.filterTerm) || user.email.contains(this.filterTerm));
                }),
                [
                    (user) => {
                        return user.isAdmin;
                    },
                    (user) => {
                        return user.type === 'active';
                    },
                    (user) => {
                        return user.type === 'pending';
                    },
                    (user) => {
                        return user.name;
                    }
                ],
                ['desc', 'desc', 'desc', 'asc']
            );
        },
    },
    methods: {
        selectJoinLink(): void {
            const joinLinkInput = this.$refs.joinLink as HTMLInputElement;
            joinLinkInput.select();
        },
        async copyJoinLink() {
            const joinLinkInput = this.$refs.joinLink as HTMLInputElement;
            const text = joinLinkInput.value;
            await navigator.clipboard.writeText(text);
            Toast.info(this.translate('copied_to_clipboard'));
        },
        openAddUserModal(): void {
            this.store.openModal(this.store.modals.USERS_ADD_USER);
        }
    }
});
</script>